import { useLiveQuery } from 'dexie-react-hooks'
import PropTypes from 'prop-types'
import React, { useState, useMemo, useRef } from 'react'
import { useClickAway } from 'use-click-away'
import { db } from '../../lib/db'
import { AutoCompleteList } from '../index'
import './InputModalStyle.scss'

const InputModal = ({
  value,
  placeholder,
  type,
  setValue,
  closeModal,
  autoCompleteList,
  autoCompleteData,
  strictValidation,
}) => {
  const [inputValue, setInputValue] = useState(value)
  const [autoComplete, setAutoComplete] = useState(false)
  const ref = useRef(null)

  const validateAndSetValue = (valueToValidate) => {
    const enteredValue = valueToValidate.trim()
    let isValid = true

    if (strictValidation) {
      isValid = false

      if (autoCompleteData && Array.isArray(autoCompleteData)) {
        const normalizedEntered = enteredValue.toLowerCase()
        isValid = autoCompleteData.some((opt) => opt.toLowerCase().trim() === normalizedEntered)
      }

      if (autoCompleteList && !autoCompleteData) {
        isValid = false
      }
    }

    if (strictValidation && !isValid) {
      setValue('')
    } else {
      setValue(enteredValue)
    }

    closeModal()
  }

  useClickAway(ref, () => {
    validateAndSetValue(inputValue)
  })

  const clientList = useLiveQuery(async () => {
    if (inputValue && autoCompleteList && !autoCompleteData) {
      const clts = await db.clients
        .where('displayName')
        .startsWithIgnoreCase(inputValue)
        .offset(0)
        .limit(5)
        .toArray()
      return clts
    }
    return []
  }, [inputValue, autoCompleteList, autoCompleteData])

  const addressList = useMemo(() => {
    if (inputValue && autoCompleteData && Array.isArray(autoCompleteData)) {
      const inputClean = inputValue.toLowerCase().trim()
      return autoCompleteData
        .filter((address) => address.toLowerCase().trim().startsWith(inputClean))
        .slice(0, 5)
    }
    return []
  }, [inputValue, autoCompleteData])

  const list = autoCompleteData ? addressList : clientList

  return (
    <div className="input-modal" style={{ paddingTop: autoComplete ? '10px' : '80px' }}>
      <div ref={ref} className="input-inside">
        <input
          autoFocus={true}
          type={type}
          value={inputValue}
          placeholder={placeholder}
          onChange={(e) => {
            setInputValue(e.target.value)
            if (e.target.value && (autoCompleteList || autoCompleteData)) {
              setAutoComplete(true)
            } else {
              setAutoComplete(false)
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              validateAndSetValue(e.target.value)
            }
          }}
        />

        {autoComplete && (autoCompleteList || autoCompleteData) && list?.length > 0 && (
          <AutoCompleteList
            list={list}
            isAddressList={!!autoCompleteData}
            select={(content) => {
              setValue(content)
              setAutoComplete(false)
              closeModal()
            }}
          />
        )}
      </div>
    </div>
  )
}

InputModal.propTypes = {
  value: PropTypes.string,
  placeholder: PropTypes.string,
  type: PropTypes.string.isRequired,
  setValue: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
  autoCompleteList: PropTypes.bool,
  autoCompleteData: PropTypes.arrayOf(PropTypes.string),
  strictValidation: PropTypes.bool,
}

InputModal.defaultProps = {
  value: '',
  autoCompleteList: false,
  autoCompleteData: null,
  placeholder: '',
  strictValidation: false,
}

export default InputModal
